// Per-user, per-farm manual device order.
//
// The device grid has no stable sort at any layer — it simply mirrors the latest
// live snapshot (relay connect order × the farm agent's ADB enumeration order), so the
// cards "float" whenever a fresh snapshot arrives, which is most visible after leaving
// and returning to a farm. This module lets each operator arrange the phones by drag &
// drop and remembers that arrangement.
//
// Storage is browser localStorage, namespaced by the logged-in user id so two users on
// the same machine keep separate orders. It holds an ordered list of udids per SCOPE —
// a farm id, or 'workspace' for the flat My Devices board, which mixes farms and so
// needs an arrangement of its own;
// `sortBySavedOrder` applies it as a stable comparator (saved udids in their saved
// position, everything else appended in its incoming order) so a new/unknown device
// lands at the end instead of reshuffling the arrangement.
import { appState } from '../core/state';
const KEY_PREFIX = 'mf.devOrder';
// True while a drag-reorder is in progress. A native HTML5 drag doesn't block the event
// loop, so the background device poll keeps firing and would re-render/re-sort the grid to
// the *previous* saved order — snapping the dragged card back mid-drag so it never appears
// to move. The grid renderers (farms-view onDevicesChange, duo injectDuoDevices) check this
// and skip the DOM rebuild while a reorder is live; the next poll after `dragend` renders
// the just-saved order.
let reordering = false;
export function isReordering() {
    return reordering;
}
function storageKey(scope) {
    const uid = appState.currentUserId ?? 'anon';
    return `${KEY_PREFIX}.${uid}.${scope}`;
}
export function loadOrder(farmId) {
    try {
        const raw = localStorage.getItem(storageKey(farmId));
        if (!raw)
            return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
    }
    catch {
        return [];
    }
}
export function saveOrder(farmId, udids) {
    try {
        localStorage.setItem(storageKey(farmId), JSON.stringify(udids));
    }
    catch {
        /* quota / disabled storage — ordering is best-effort, never block the UI */
    }
}
/**
 * Stable sort of `items` by the saved order for `farmId`: udids present in the saved
 * list come first in their saved position; every other item keeps its incoming relative
 * order and is appended after. Returns a NEW array (input untouched). A null/invalid
 * farmId (landing list) is a no-op passthrough.
 */
export function sortBySavedOrder(farmId, items, getUdid) {
    if (farmId == null)
        return items.slice();
    const order = loadOrder(farmId);
    if (order.length === 0)
        return items.slice();
    const rank = new Map(order.map((udid, i) => [udid, i]));
    const NONE = Number.MAX_SAFE_INTEGER;
    // Stable: decorate with original index, sort by (savedRank, originalIndex).
    return items
        .map((item, i) => ({ item, i, r: rank.get(getUdid(item)) ?? NONE }))
        .sort((a, b) => (a.r - b.r) || (a.i - b.i))
        .map((x) => x.item);
}
/**
 * Enable drag-and-drop reordering of the `[data-udid]` cards inside `gridEl` and persist
 * the resulting order for `farmId`. Idempotent per grid element (guarded so the duo
 * poll, which re-runs on the same grid, doesn't stack listeners).
 *
 * Uses Pointer Events, NOT native HTML5 `draggable`. Native DnD is unreliable here: the
 * cards embed a `<video>` (live scrcpy) or an `<iframe>` (duo screen), and a drag that
 * begins near/over those elements silently fails to fire `dragover`, so the card "picks
 * up" but never moves. Pointer Events hit-test with `elementFromPoint` and are immune to
 * that — so the cards must NOT be `draggable` (device-card.ts / duo-devices.ts).
 *
 * Only the card header (the title zone) is the handle; a press on an interactive control
 * (button/input/iframe/video…) is ignored so controls and embedded screens keep working.
 * The reorder only begins after the pointer moves past a small threshold, so a plain
 * click on the header never starts a drag.
 */
const DRAG_THRESHOLD_PX = 6;
export function wireGridDragReorder(gridEl, farmId) {
    if (gridEl.dataset['dndWired'] === '1')
        return;
    gridEl.dataset['dndWired'] = '1';
    const cardOf = (t) => {
        const el = t?.closest('[data-udid]') ?? null;
        return el && el.parentElement === gridEl ? el : null;
    };
    let dragging = null;
    let startX = 0;
    let startY = 0;
    let active = false; // true once the pointer passed the threshold and a reorder is live
    const onMove = (e) => {
        if (!dragging)
            return;
        if (!active) {
            if (Math.abs(e.clientX - startX) + Math.abs(e.clientY - startY) < DRAG_THRESHOLD_PX)
                return;
            active = true;
            reordering = true; // pause poll-driven grid rebuilds until pointerup
            dragging.classList.add('dragging');
        }
        e.preventDefault();
        // The dragged card keeps its slot (dimmed); find whichever card is under the
        // pointer and slot the dragged one before/after it by horizontal midpoint.
        const over = cardOf(document.elementFromPoint(e.clientX, e.clientY));
        if (over && over !== dragging) {
            const rect = over.getBoundingClientRect();
            const after = e.clientX > rect.left + rect.width / 2;
            gridEl.insertBefore(dragging, after ? over.nextSibling : over);
        }
    };
    const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        if (dragging && active) {
            dragging.classList.remove('dragging');
            const udids = [];
            gridEl.querySelectorAll(':scope > [data-udid]').forEach((el) => {
                const u = el.dataset['udid'];
                if (u)
                    udids.push(u);
            });
            if (udids.length)
                saveOrder(farmId, udids);
        }
        dragging = null;
        active = false;
        reordering = false; // resume poll-driven grid rebuilds
    };
    gridEl.addEventListener('pointerdown', (e) => {
        if (e.button !== 0)
            return; // left button / primary touch only
        const target = e.target;
        // Only the header is the handle; never start on the stream body or a control.
        if (!target.closest('.device-card__header'))
            return;
        if (target.closest('button, input, a, select, textarea, label, iframe, video'))
            return;
        const card = cardOf(target);
        if (!card)
            return;
        dragging = card;
        startX = e.clientX;
        startY = e.clientY;
        active = false;
        // Track on `document` (not via pointer capture) so the drag keeps working if the
        // pointer briefly leaves the grid; listeners are torn down on pointerup.
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
    });
}
